import { Button } from "@mui/joy";
import CustomModal from "components/common/CustomModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import useGetEventFromApp from "hooks/useGetEventFromApp";
import { useCallback, useEffect, useState } from "react";
import { USER_DETAIL_ENDPOINT, USER_DOMAIN, userApi } from "api/userApi";
import { paymentOrderAtom } from "recoil/paymentOrderAtom";
import { PATH } from "routes/paths";
import { useNavigate } from "react-router-dom";
import useMixpanelWeb from "hooks/useMixpanelWeb";
import { PlanKey } from "../planTypes";
import { isAuthReady } from "../../../../api";
import { userAtom } from "../../../../recoil/userAtom";

export default function PlanUpgradeButton({
  currentPlan,
  nextPlan,
}: {
  currentPlan: PlanKey;
  nextPlan: PlanKey;
}) {
  const isInappWebview = useIsInappWebview();

  const navigate = useNavigate();
  const [user, setUser] = useRecoilState(userAtom);
  const paymentOrderState = useRecoilValue(paymentOrderAtom);
  const parsedMessageFromApp = useGetEventFromApp();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false); //중복 방지
  const [disabled, setDisabled] = useState<boolean>(false);
  const [coachModal, setCoachModal] = useState<"none" | "keep" | "change">(
    "none"
  );

  const setMixPanel = useMixpanelWeb({
    eventName: "프로그램 변경하기 버튼 클릭",
  });

  useQuery(
    [USER_DOMAIN, USER_DETAIL_ENDPOINT],
    useCallback(() => userApi.getDetail(), []),
    {
      enabled: isAuthReady(user),

      onSuccess: (data) => {
        if (data && data.result) {
          //결제 탭 닫기
          if (data.result.subscription?.hasBeenPlanUpgraded) {
            setDisabled(true);
          }

          setUser((user) => ({
            ...user,
            ...data.result,
          }));
        }
      },
    }
  );

  useEffect(() => {
    if (parsedMessageFromApp?.event === "payment") {
      const status = parsedMessageFromApp.params.status;
      if (status) {
        setIsLoading(false);
        if (status === "success" && !isDone) {
          queryClient.invalidateQueries([USER_DOMAIN, USER_DETAIL_ENDPOINT]);
          setIsDone(true);
        }
      }
    }
  }, [parsedMessageFromApp]);

  const buttonText = "프로그램 변경하기";

  const requestPayment = (isCoachChangeRequested: boolean) => {
    if (paymentOrderState) {
      if (paymentOrderState?.paymentProvider === "toss_payments") {
        const query = `previous_plan=${currentPlan}&new_plan=${nextPlan}&is_coach_change_requested=${isCoachChangeRequested}`;
        if (isInappWebview) {
          setIsLoading(true);

          sendMessage(
            JSON.stringify({
              event: "request_toss_payments",
              params: {
                url: `${window.location.origin}${PATH.tossPaymentsPlanUpgrade}?${query}`,
              },
            })
          );

          setTimeout(() => {
            setIsLoading(false);
          }, 3000);
        } else {
          navigate(`${PATH.tossPaymentsPlanUpgrade}?${query}`);
        }
      }

      setMixPanel();
    }
  };

  // 마스터 선택 시 담당 코치 관련 안내 모달을 먼저 띄운다.
  const handlePrimaryClick = () => {
    if (nextPlan === "master") {
      setCoachModal(
        paymentOrderState?.assignedCoachStatus === "master_unavailable"
          ? "change"
          : "keep"
      );
      return;
    }
    requestPayment(false);
  };

  return (
    <>
      <Button
        loading={isLoading}
        disabled={disabled}
        onClick={handlePrimaryClick}
        size={"lg"}
        color={"primary"}
        sx={{ width: "100%", fontWeight: 700 }}
      >
        {buttonText}
      </Button>

      <CustomModal
        open={coachModal === "keep"}
        onClose={() => setCoachModal("none")}
        title={"담당 코치를 유지할 수 있어요"}
        subtitle={
          "마스터 플랜은 수석 코치가 배정되지만, 원하신다면 현재 담당 코치를 유지한 채 플랜만 업그레이드할 수 있습니다."
        }
        actions={[
          {
            label: "코치 유지하기",
            onClick: () => {
              setCoachModal("none");
              requestPayment(false);
            },
            variant: "Solid",
          },
          {
            label: "변경하기",
            onClick: () => {
              setCoachModal("none");
              requestPayment(true);
            },
            variant: "Plain",
          },
        ]}
      />

      <CustomModal
        open={coachModal === "change"}
        onClose={() => setCoachModal("none")}
        title={"담당 코치가 변경돼요"}
        subtitle={
          "마스터 플랜은 수석 코치가 배정되는 플랜입니다. 플랜 업그레이드 시 담당 코치가 수석 코치로 변경됩니다."
        }
        actions={[
          {
            label: "계속하기",
            onClick: () => {
              setCoachModal("none");
              requestPayment(true);
            },
            variant: "Solid",
          },
        ]}
      />
    </>
  );
}
